import { ACTIVE_SUBSCRIPTION_STATUSES } from "./config";
import { OrganizationBillingPlan } from "./types";

import type { OrganizationSubscription } from "./types";

export const FREE_SURVEY_REPORT_LIMIT = 5;

export type ReportExportSource = "free" | "paid" | "internal" | null;

export interface ReportExportEntitlement {
  canExport: boolean;
  source: ReportExportSource;
  freeSurveyLimit: number;
  freeSurveysUsed: number;
  freeSurveysRemaining: number;
  surveyCoveredByFreeAllowance: boolean;
  paidExportAvailable: boolean;
  message: string;
}

export const resolveReportExportEntitlement = ({
  freeSurveysUsed,
  surveyCoveredByFreeAllowance,
  surveyAllowanceConsumedAt,
  subscription,
}: {
  freeSurveysUsed: number;
  surveyCoveredByFreeAllowance: boolean;
  surveyAllowanceConsumedAt: Date | null;
  subscription: OrganizationSubscription | null;
}): ReportExportEntitlement => {
  const normalizedFreeSurveysUsed = Math.max(freeSurveysUsed, 0);
  const freeSurveysRemaining = Math.max(
    FREE_SURVEY_REPORT_LIMIT - normalizedFreeSurveysUsed,
    0,
  );
  const isInternal = subscription?.plan === OrganizationBillingPlan.INTERNAL;
  const paidSubscriptionActive = Boolean(
    subscription && ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status),
  );
  const paidAllowanceRemaining = Boolean(
    subscription &&
      paidSubscriptionActive &&
      subscription.reportsUsed < subscription.annualReportAllowance,
  );
  const paidExportAvailable =
    isInternal || surveyAllowanceConsumedAt !== null || paidAllowanceRemaining;

  if (surveyCoveredByFreeAllowance) {
    return {
      canExport: true,
      source: "free",
      freeSurveyLimit: FREE_SURVEY_REPORT_LIMIT,
      freeSurveysUsed: normalizedFreeSurveysUsed,
      freeSurveysRemaining,
      surveyCoveredByFreeAllowance: true,
      paidExportAvailable,
      message: "You can regenerate this report as often as you need.",
    };
  }

  if (isInternal) {
    return {
      canExport: true,
      source: "internal",
      freeSurveyLimit: FREE_SURVEY_REPORT_LIMIT,
      freeSurveysUsed: normalizedFreeSurveysUsed,
      freeSurveysRemaining,
      surveyCoveredByFreeAllowance: false,
      paidExportAvailable: true,
      message: "Your organization can generate reports.",
    };
  }

  if (surveyAllowanceConsumedAt) {
    return {
      canExport: true,
      source: "paid",
      freeSurveyLimit: FREE_SURVEY_REPORT_LIMIT,
      freeSurveysUsed: normalizedFreeSurveysUsed,
      freeSurveysRemaining,
      surveyCoveredByFreeAllowance: false,
      paidExportAvailable: true,
      message: "You can regenerate this report as often as you need.",
    };
  }

  if (freeSurveysRemaining > 0) {
    return {
      canExport: true,
      source: "free",
      freeSurveyLimit: FREE_SURVEY_REPORT_LIMIT,
      freeSurveysUsed: normalizedFreeSurveysUsed,
      freeSurveysRemaining,
      surveyCoveredByFreeAllowance: false,
      paidExportAvailable,
      message:
        freeSurveysRemaining === FREE_SURVEY_REPORT_LIMIT
          ? "Generate reports for your first five surveys free. No subscription required."
          : `You can generate reports for ${freeSurveysRemaining} more surveys free.`,
    };
  }

  if (paidExportAvailable) {
    return {
      canExport: true,
      source: "paid",
      freeSurveyLimit: FREE_SURVEY_REPORT_LIMIT,
      freeSurveysUsed: normalizedFreeSurveysUsed,
      freeSurveysRemaining,
      surveyCoveredByFreeAllowance: false,
      paidExportAvailable: true,
      message: "Your subscription includes report exports.",
    };
  }

  return {
    canExport: false,
    source: null,
    freeSurveyLimit: FREE_SURVEY_REPORT_LIMIT,
    freeSurveysUsed: normalizedFreeSurveysUsed,
    freeSurveysRemaining,
    surveyCoveredByFreeAllowance: false,
    paidExportAvailable: false,
    message:
      "You've used your five free surveys. Subscribe to generate reports for additional surveys.",
  };
};
